import { useMutation } from "@tanstack/react-query";
import { Check, HelpCircle, Loader2, RotateCw, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  humanizeFeatureName,
  humanizeValue,
  OPERATOR_LABELS_PT_BR,
} from "../candidate-intelligence/results/format-scores";
import {
  DIM_LABEL,
  type FeaturePredicate,
  type OptimizationProposal,
  type RefitHints,
} from "./optimizer-types";

/** Inline help icon with a native tooltip. Used to explain jargon-y metric
 *  labels (LOO, QWK, fixtures, etc.) without committing to a full popover
 *  library. Hover/focus surfaces the explanation. */
function InfoTip({ text }: { text: string }) {
  return (
    <span className="optimizer-info-tip" title={text} aria-label={text} role="img">
      <HelpCircle className="h-3 w-3" aria-hidden />
    </span>
  );
}

/**
 * One expanded row in the optimizer review accordion: side-by-side LOO/QWK
 * metrics for current vs proposed, the rung diff, the per-fixture grade
 * change table, the corrections-coverage table, and three action buttons.
 *
 * The component is purely presentational + dispatches mutations against
 * `/api/labs/calibration-optimizer/decision`. State (accepted / rejected /
 * fitting) lives on the server in the optimizer job file; the parent
 * polls `/status` and re-renders us when proposals change.
 */
/**
 * Compare proposed vs current metrics and label the proposal as a
 * regression when at least one core metric moves the wrong way past a
 * small tolerance. We don't flag tiny noise — a 1 p.p. LOO drop is
 * within bootstrap CI for the corpus sizes we run on, but an 8 p.p.
 * drop with rising silver violations is unambiguously worse.
 */
function detectRegression(proposal: OptimizationProposal): {
  isRegression: boolean;
  reasons: string[];
} {
  if (proposal.status !== "ready" && proposal.status !== "accepted") {
    return { isRegression: false, reasons: [] };
  }
  if (!proposal.proposed) return { isRegression: false, reasons: [] };
  const cur = proposal.current.metrics;
  const next = proposal.proposed.metrics;
  const reasons: string[] = [];

  const looDelta = (next.looHitRate ?? 0) - (cur.looHitRate ?? 0);
  // 3 p.p. is the rough bootstrap noise floor at our corpus size; anything
  // worse is a real regression.
  if (looDelta <= -0.03) {
    reasons.push(`acerto fora da amostra caiu ${Math.round(Math.abs(looDelta) * 100)} p.p.`);
  }

  const curQwk = cur.qwk;
  const nextQwk = next.qwk;
  if (typeof curQwk === "number" && typeof nextQwk === "number" && nextQwk - curQwk <= -0.05) {
    reasons.push(`concordância ponderada caiu ${(curQwk - nextQwk).toFixed(2)}`);
  }

  if ((next.silverViolations ?? 0) > (cur.silverViolations ?? 0)) {
    const delta = (next.silverViolations ?? 0) - (cur.silverViolations ?? 0);
    reasons.push(`+${delta} rejeições incorretas`);
  }

  return { isRegression: reasons.length > 0, reasons };
}

export function DimProposalPanel({ proposal }: { proposal: OptimizationProposal }) {
  const [refitOpen, setRefitOpen] = useState(false);
  // Operator escape hatch when the regression banner fires. False by
  // default so the Accept button stays disabled until the operator
  // explicitly opts into shipping the regressing proposal.
  const [regressionOverride, setRegressionOverride] = useState(false);

  const decision = useMutation({
    mutationFn: async (vars: {
      action: "accept" | "reject" | "refit";
      refitHints?: RefitHints;
    }) => {
      const res = await apiRequest("POST", "/api/labs/calibration-optimizer/decision", {
        dim: proposal.dim,
        action: vars.action,
        ...(vars.refitHints ? { refitHints: vars.refitHints } : {}),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/labs/calibration-optimizer/status"] });
    },
  });

  if (proposal.status === "error") {
    return (
      <div className="optimizer-dim-error">
        <strong>Falha ao otimizar {DIM_LABEL[proposal.dim]}.</strong>
        <span className="text-muted-foreground">{proposal.errorMessage}</span>
      </div>
    );
  }
  if (proposal.status === "pending" || proposal.status === "fitting") {
    return (
      <div className="optimizer-dim-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <span>
          {proposal.status === "pending"
            ? `na fila para ${DIM_LABEL[proposal.dim]}…`
            : `ajustando rubrica de ${DIM_LABEL[proposal.dim]}…`}
        </span>
      </div>
    );
  }
  if (!proposal.proposed) {
    return (
      <div className="optimizer-dim-error">
        Sem proposta para {DIM_LABEL[proposal.dim]} — recarregue a otimização.
      </div>
    );
  }

  const isDecided = proposal.status === "accepted" || proposal.status === "rejected";
  const regression = detectRegression(proposal);
  return (
    <div className={`optimizer-dim-panel ${isDecided ? `is-${proposal.status}` : ""}`}>
      {regression.isRegression && (
        <div
          className="optimizer-regression-banner"
          role="alert"
          data-testid={`optimizer-regression-${proposal.dim}`}
        >
          <strong>Proposta regride a rubrica atual.</strong>
          <span>{regression.reasons.join(" · ")}</span>
          <label className="optimizer-regression-override">
            <input
              type="checkbox"
              checked={regressionOverride}
              onChange={(e) => setRegressionOverride(e.target.checked)}
              data-testid={`optimizer-regression-override-${proposal.dim}`}
            />
            Aceitar mesmo assim
          </label>
        </div>
      )}
      <div className="optimizer-dim-metrics">
        <MetricCompare
          label="Acerto fora da amostra"
          tooltip="Para cada perfil de referência, retira ele do treino, refita a rubrica nos demais, e mede se a previsão cai dentro da nota esperada. Mede generalização — números altos em treino mas baixos aqui significam overfit."
          current={proposal.current.metrics.looHitRate}
          proposed={proposal.proposed.metrics.looHitRate}
          format={pct}
          ci={{
            current: proposal.current.metrics.looCi,
            proposed: proposal.proposed.metrics.looCi,
          }}
        />
        <MetricCompare
          label="Concordância ponderada"
          tooltip="Kappa quadrático de Cohen sobre o acerto fora da amostra. Penaliza erros grandes mais que pequenos. 1.00 = concordância perfeita; 0 = nível de acaso; negativo = pior que jogar moeda. Quebra empates quando duas rubricas têm o mesmo acerto."
          current={proposal.current.metrics.qwk}
          proposed={proposal.proposed.metrics.qwk}
          format={(n) => (n === null ? "n/a" : n.toFixed(2))}
        />
        <MetricCompare
          label="Acerto nos exemplos de treino"
          tooltip="Mesmo perfis usados no treino — número sempre alto e enganoso. Olhe junto com o 'fora da amostra' para detectar overfit."
          current={proposal.current.metrics.goldHitRate}
          proposed={proposal.proposed.metrics.goldHitRate}
          format={pct}
        />
        <MetricCompare
          label="Quantidade de regras"
          tooltip="Cada regra é uma cláusula 'se feature X, nota = N'. Menos regras geralmente significa rubrica mais legível e generaliza melhor; mais regras pode ser overfit."
          current={proposal.current.metrics.rungCount}
          proposed={proposal.proposed.metrics.rungCount}
          format={(n) => String(n)}
        />
        <MetricCompare
          label="Rejeições incorretas"
          tooltip="Perfis marcados como 'rejeitar' no corpus que a nova rubrica daria nota acima de 2. Quanto menor melhor — esse número subindo é um sinal de regressão."
          current={proposal.current.metrics.silverViolations}
          proposed={proposal.proposed.metrics.silverViolations}
          format={(n) => String(n)}
          invert
        />
      </div>

      <RungDiff proposal={proposal} />
      <FixturesTable proposal={proposal} />
      <CorrectionsTable proposal={proposal} />

      {refitOpen && (
        <RefitHintsForm
          proposal={proposal}
          onCancel={() => setRefitOpen(false)}
          onSubmit={(hints) => {
            setRefitOpen(false);
            decision.mutate({ action: "refit", refitHints: hints });
          }}
        />
      )}

      <div className="optimizer-dim-actions">
        <Button
          variant={proposal.status === "accepted" ? "default" : "outline"}
          size="sm"
          onClick={() => decision.mutate({ action: "accept" })}
          // Regression guard: disable Accept on a strictly-worse proposal
          // unless the operator has already toggled the override. Pre-fix,
          // committing a regressing rubric (e.g. HR Fluency 88% → 80%
          // accuracy with 9 extra wrong-rejections) silently shipped a
          // worse model. Operators that want to commit anyway can flip
          // the override directly below the metric panel.
          disabled={
            decision.isPending ||
            (regression.isRegression && proposal.status !== "accepted" && !regressionOverride)
          }
          data-testid={`optimizer-accept-${proposal.dim}`}
        >
          <Check className="h-3.5 w-3.5 mr-2" aria-hidden />
          {proposal.status === "accepted" ? "Aceito" : "Aceitar"}
        </Button>
        <Button
          variant={proposal.status === "rejected" ? "default" : "outline"}
          size="sm"
          onClick={() => decision.mutate({ action: "reject" })}
          disabled={decision.isPending}
          data-testid={`optimizer-reject-${proposal.dim}`}
        >
          <X className="h-3.5 w-3.5 mr-2" aria-hidden />
          {proposal.status === "rejected" ? "Recusado" : "Recusar"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setRefitOpen((v) => !v)}
          disabled={decision.isPending}
          data-testid={`optimizer-refit-${proposal.dim}`}
        >
          <RotateCw className="h-3.5 w-3.5 mr-2" aria-hidden />
          Refazer com ajustes
        </Button>
      </div>
    </div>
  );
}

function MetricCompare({
  label,
  tooltip,
  current,
  proposed,
  format,
  formatDelta,
  invert,
  ci,
}: {
  label: string;
  tooltip?: string;
  current: number | null;
  proposed: number | null;
  format: (n: number | null) => string;
  /** Optional override for the delta label. Defaults to a signed % or
   *  a signed integer depending on the formatter's range. */
  formatDelta?: (delta: number) => string;
  /** When true, lower-is-better (e.g. silver violations). */
  invert?: boolean;
  ci?: {
    current: { lo: number; hi: number };
    proposed: { lo: number; hi: number };
  };
}) {
  const delta =
    current !== null && proposed !== null && Number.isFinite(current) && Number.isFinite(proposed)
      ? proposed - current
      : null;
  const isZero = delta !== null && Math.abs(delta) < 1e-9;
  const better = delta === null ? null : isZero ? null : invert ? delta < 0 : delta > 0;
  const tone = delta === null ? "" : isZero ? "is-flat" : better ? "is-up" : "is-down";
  return (
    <div className={`optimizer-metric ${tone}`}>
      <div className="optimizer-metric-label">
        {label}
        {tooltip ? <InfoTip text={tooltip} /> : null}
      </div>
      <div className="optimizer-metric-values tabular-nums">
        <span>{format(current)}</span>
        <span className="optimizer-metric-arrow" aria-hidden>
          →
        </span>
        <strong>{format(proposed)}</strong>
        {delta !== null && (
          <span
            className={`optimizer-metric-delta ${tone}`}
            title={better === null ? "sem alteração" : better ? "melhorou" : "piorou"}
          >
            {isZero
              ? "= sem mudança"
              : `${better ? "↑" : "↓"} ${formatDeltaDefault(delta, format, formatDelta)}`}
          </span>
        )}
      </div>
      {ci && (
        <div className="optimizer-metric-ci tabular-nums">
          [{pct(ci.current.lo)}, {pct(ci.current.hi)}] → [{pct(ci.proposed.lo)},{" "}
          {pct(ci.proposed.hi)}]
        </div>
      )}
    </div>
  );
}

/** Default delta formatter: percentage-with-sign when the format produces
 *  a `%` string (LOO, in-sample); fixed-decimal otherwise (QWK, counts).
 *  Caller can override via `formatDelta` if neither shape fits. */
function formatDeltaDefault(
  delta: number,
  format: (n: number | null) => string,
  override?: (delta: number) => string,
): string {
  if (override) return override(delta);
  const sample = format(0.123);
  const isPct = sample.endsWith("%");
  const abs = Math.abs(delta);
  if (isPct) return `${(abs * 100).toFixed(0)} p.p.`;
  if (Number.isInteger(delta)) return String(abs);
  return abs.toFixed(2);
}

function pct(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "n/a";
  return `${(n * 100).toFixed(0)}%`;
}

/** Render a predicate using human-readable feature names, operator symbols,
 *  and translated boolean values. Falls back to the snake_case → words
 *  format when the feature isn't in FEATURE_LABELS_PT_BR. */
function predicateToText(pred: FeaturePredicate[]): string {
  if (pred.length === 0) return "(sempre)";
  return pred
    .map((p) => {
      const label = humanizeFeatureName(p.feature);
      const op = OPERATOR_LABELS_PT_BR[p.op] ?? p.op;
      const value = humanizeValue(String(p.value));
      return `${label} ${op} ${value}`;
    })
    .join(" e ");
}

function RungDiff({ proposal }: { proposal: OptimizationProposal }) {
  if (!proposal.diff || !proposal.proposed) return null;
  const { addedRungs, removedRungs, changedRungs } = proposal.diff;
  if (addedRungs.length + removedRungs.length + changedRungs.length === 0) {
    return (
      <details className="optimizer-rung-diff">
        <summary>
          Regras da rubrica <span className="text-muted-foreground">(idênticas)</span>
        </summary>
        <p className="text-muted-foreground text-xs">
          O ajuste não mudou nenhuma regra — só os parâmetros internos do agente. Aceitar não vai
          alterar a rubrica.
        </p>
      </details>
    );
  }
  return (
    <details className="optimizer-rung-diff" open>
      <summary>
        Regras da rubrica
        <InfoTip text="Cada regra diz: 'se a condição X é verdadeira, atribua nota N'. O agente aplica de cima para baixo (notas mais altas primeiro)." />
        <span className="optimizer-rung-diff-counts">
          {addedRungs.length > 0 && (
            <span className="is-add" title="regras novas">
              +{addedRungs.length}
            </span>
          )}
          {removedRungs.length > 0 && (
            <span className="is-rem" title="regras removidas">
              −{removedRungs.length}
            </span>
          )}
          {changedRungs.length > 0 && (
            <span className="is-chg" title="regras alteradas">
              ~{changedRungs.length}
            </span>
          )}
        </span>
      </summary>
      <ul className="optimizer-rung-list">
        {changedRungs.map((c) => (
          <li key={`chg-${c.grade}`} className="optimizer-rung-row is-chg">
            <span className="optimizer-rung-grade tabular-nums">L{c.grade}</span>
            <span className="optimizer-rung-prose">
              <span className="optimizer-rung-before">{predicateToText(c.before.when)}</span>
              <span className="optimizer-rung-arrow" aria-hidden>
                {" → "}
              </span>
              <span className="optimizer-rung-after">{predicateToText(c.after.when)}</span>
            </span>
          </li>
        ))}
        {addedRungs.map((r) => (
          <li key={`add-${r.grade}`} className="optimizer-rung-row is-add">
            <span className="optimizer-rung-grade tabular-nums">L{r.grade}</span>
            <span className="optimizer-rung-prose">{predicateToText(r.when)}</span>
          </li>
        ))}
        {removedRungs.map((r) => (
          <li key={`rem-${r.grade}`} className="optimizer-rung-row is-rem">
            <span className="optimizer-rung-grade tabular-nums">L{r.grade}</span>
            <span className="optimizer-rung-prose">{predicateToText(r.when)}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function FixturesTable({ proposal }: { proposal: OptimizationProposal }) {
  if (proposal.fixturesAffected.length === 0) return null;
  const changed = proposal.fixturesAffected.filter(
    (f) => f.currentGrade !== f.proposedGrade && f.proposedGrade !== null,
  );
  if (changed.length === 0) {
    return (
      <details className="optimizer-fixtures">
        <summary>
          Perfis de referência{" "}
          <span className="text-muted-foreground">(nenhuma mudança de nota)</span>
        </summary>
        <p className="text-xs text-muted-foreground">
          Os {proposal.fixturesAffected.length} perfis de referência continuam recebendo a mesma
          nota com a nova rubrica.
        </p>
      </details>
    );
  }
  return (
    <details className="optimizer-fixtures" open>
      <summary>
        Perfis cuja nota muda
        <InfoTip text="Perfis de referência (fixtures) cujo nível de saída ficaria diferente com a nova rubrica. Verifique se as mudanças fazem sentido antes de aceitar." />
        <span className="optimizer-rung-diff-counts">
          <span className="is-chg">{changed.length}</span>
          <span className="text-muted-foreground">/ {proposal.fixturesAffected.length}</span>
        </span>
      </summary>
      <table className="optimizer-table">
        <thead>
          <tr>
            <th>Candidato</th>
            <th className="text-right">Nota atual</th>
            <th className="text-right">Nova nota</th>
            <th className="text-right">Alvo da CHRO</th>
            <th>Dentro do alvo?</th>
          </tr>
        </thead>
        <tbody>
          {changed.map((f) => (
            <tr key={f.candidateId}>
              <td>{f.candidateId}</td>
              <td className="text-right tabular-nums">{fmtGrade(f.currentGrade)}</td>
              <td className="text-right tabular-nums">
                <strong>{fmtGrade(f.proposedGrade)}</strong>
              </td>
              <td className="text-right tabular-nums">{fmtGrade(f.targetGrade)}</td>
              <td>
                {f.withinHitRange === null ? (
                  "—"
                ) : f.withinHitRange ? (
                  <span className="optimizer-hit-yes">✓</span>
                ) : (
                  <span className="optimizer-hit-no">✗</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function CorrectionsTable({ proposal }: { proposal: OptimizationProposal }) {
  if (proposal.correctionsCovered.length === 0) {
    return (
      <details className="optimizer-corrections">
        <summary>
          Correções da CHRO nesta rubrica <span className="text-muted-foreground">(nenhuma)</span>
        </summary>
      </details>
    );
  }
  const hit = proposal.correctionsCovered.filter((c) => c.withinTolerance === true).length;
  return (
    <details className="optimizer-corrections" open>
      <summary>
        Correções da CHRO nesta rubrica
        <InfoTip text="Cada correção representa uma vez que a CHRO marcou um candidato com nota diferente da que o modelo deu. A nova rubrica acerta o alvo quando a previsão fica a menos de 0.5 ponto da nota marcada pela CHRO." />
        <span className="optimizer-rung-diff-counts">
          <span className="optimizer-hit-yes">{hit}</span>
          <span className="text-muted-foreground">
            / {proposal.correctionsCovered.length} acertaram o alvo
          </span>
        </span>
      </summary>
      <table className="optimizer-table">
        <thead>
          <tr>
            <th>Candidato</th>
            <th className="text-right">Alvo CHRO</th>
            <th className="text-right">Nova nota</th>
            <th>Acertou o alvo?</th>
          </tr>
        </thead>
        <tbody>
          {proposal.correctionsCovered.map((c) => (
            <tr key={c.correctionId}>
              <td>{c.candidateName ?? <em>(sem perfil)</em>}</td>
              <td className="text-right tabular-nums">{c.targetGrade.toFixed(1)}</td>
              <td className="text-right tabular-nums">
                <strong>{fmtGrade(c.proposedGrade)}</strong>
              </td>
              <td>
                {c.withinTolerance === null ? (
                  "—"
                ) : c.withinTolerance ? (
                  <span className="optimizer-hit-yes">✓</span>
                ) : (
                  <span className="optimizer-hit-no">✗</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function fmtGrade(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return n.toFixed(1);
}

function RefitHintsForm({
  proposal,
  onSubmit,
  onCancel,
}: {
  proposal: OptimizationProposal;
  onSubmit: (hints: RefitHints) => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const features = (() => {
    const set = new Set<string>();
    if (proposal.proposed) {
      for (const r of proposal.proposed.rungs) for (const p of r.when) set.add(p.feature);
    }
    return Array.from(set).sort();
  })();
  const [blacklist, setBlacklist] = useState<Set<string>>(new Set());
  const [lockedGrades, setLockedGrades] = useState<Set<number>>(new Set());
  const [parsimony, setParsimony] = useState<number>(0.1);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackPreview, setFeedbackPreview] = useState<{
    hints: RefitHints;
    unparsed: string[];
  } | null>(null);

  const parseFeedback = useMutation({
    mutationFn: async (feedback: string) => {
      const res = await apiRequest("POST", "/api/labs/calibration-optimizer/parse-feedback", {
        dim: proposal.dim,
        feedback,
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        hints?: RefitHints;
        unparsed?: string[];
      };
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      return { hints: body.hints ?? {}, unparsed: body.unparsed ?? [] };
    },
    onSuccess: (result) => {
      setFeedbackPreview(result);
      if (result.hints.blacklistedFeatures) {
        setBlacklist((prev) => {
          const next = new Set(prev);
          for (const f of result.hints.blacklistedFeatures ?? []) next.add(f);
          return next;
        });
      }
      if (result.hints.lockedRungs) {
        setLockedGrades((prev) => {
          const next = new Set(prev);
          for (const r of result.hints.lockedRungs ?? []) next.add(r.grade);
          return next;
        });
      }
      if (typeof result.hints.parsimony === "number") setParsimony(result.hints.parsimony);
    },
    onError: (err: Error) => {
      toast({ title: "Falha ao interpretar", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="optimizer-refit-form">
      <h4>Ajustes para o próximo refit</h4>
      <p className="text-xs text-muted-foreground">
        Refit usa o mesmo corpus e o mesmo set de correções; só os hints abaixo mudam.
      </p>
      <div className="optimizer-refit-section">
        <strong className="optimizer-refit-section-title">
          Pergunte ao agente (em linguagem natural)
        </strong>
        <p className="text-xs text-muted-foreground">
          Descreva o que você quer ajustar. O agente interpreta e marca os campos abaixo. Exemplo:
          "não use o sinal de anos na empresa, mantenha a regra de L4".
        </p>
        <textarea
          className="optimizer-refit-feedback"
          placeholder="Ex.: simplifique as regras; ignore o sinal de universidade; mantenha L5 igual"
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          rows={3}
        />
        <div className="optimizer-refit-feedback-actions">
          <Button
            size="sm"
            variant="outline"
            onClick={() => parseFeedback.mutate(feedbackText)}
            disabled={parseFeedback.isPending || feedbackText.trim().length === 0}
            data-testid={`optimizer-parse-feedback-${proposal.dim}`}
          >
            {parseFeedback.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" aria-hidden />
            ) : null}
            Interpretar
          </Button>
        </div>
        {feedbackPreview && (
          <div className="optimizer-refit-feedback-preview">
            <strong>O agente entendeu:</strong>
            <ul>
              {(feedbackPreview.hints.blacklistedFeatures ?? []).map((f) => (
                <li key={`bl-${f}`}>
                  não usar o sinal <strong>{humanizeFeatureName(f)}</strong>{" "}
                  <code className="text-muted-foreground">({f})</code>
                </li>
              ))}
              {(feedbackPreview.hints.lockedRungs ?? []).map((r) => (
                <li key={`lk-${r.grade}`}>preservar a regra de L{r.grade}</li>
              ))}
              {typeof feedbackPreview.hints.parsimony === "number" && (
                <li>simplicidade das regras → {feedbackPreview.hints.parsimony.toFixed(2)}</li>
              )}
              {feedbackPreview.unparsed.length > 0 && (
                <li className="optimizer-refit-feedback-unparsed">
                  ignorado: {feedbackPreview.unparsed.join("; ")}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      {features.length > 0 && (
        <div className="optimizer-refit-section">
          <strong className="optimizer-refit-section-title">
            Sinais que o agente deve evitar
            <InfoTip text="Cada sinal é um pedaço de evidência extraído do perfil (anos de empresa, tier da faculdade, etc.). Marque os que você acha que o agente está usando errado." />
          </strong>
          <p className="text-xs text-muted-foreground">
            Marque sinais que parecem overfit ou pouco confiáveis no caso atual.
          </p>
          <ul className="optimizer-refit-features">
            {features.map((f) => (
              <li key={f}>
                <label>
                  <input
                    type="checkbox"
                    checked={blacklist.has(f)}
                    onChange={(e) => {
                      setBlacklist((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(f);
                        else next.delete(f);
                        return next;
                      });
                    }}
                  />
                  <span className="optimizer-refit-feature-label">
                    {humanizeFeatureName(f)} <code className="text-muted-foreground">{f}</code>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
      {proposal.proposed && proposal.proposed.rungs.length > 0 && (
        <div className="optimizer-refit-section">
          <strong className="optimizer-refit-section-title">
            Regras a preservar
            <InfoTip text="Cada regra é 'se a condição X é verdadeira, atribua nota N'. Marque as que devem ficar exatamente como estão no próximo refit." />
          </strong>
          <p className="text-xs text-muted-foreground">
            Marque regras propostas que devem ser mantidas exatamente como estão.
          </p>
          <ul className="optimizer-refit-rungs">
            {proposal.proposed.rungs.map((r) => (
              <li key={r.grade}>
                <label>
                  <input
                    type="checkbox"
                    checked={lockedGrades.has(r.grade)}
                    onChange={(e) => {
                      setLockedGrades((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(r.grade);
                        else next.delete(r.grade);
                        return next;
                      });
                    }}
                  />
                  <span className="tabular-nums">L{r.grade}</span> · {predicateToText(r.when)}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="optimizer-refit-section">
        <strong className="optimizer-refit-section-title">
          Simplicidade das regras
          <InfoTip text="Controla quanto o agente é penalizado por usar muitos sinais numa única regra. Maior = regras mais curtas (1-2 sinais); menor = regras mais ricas (3+ sinais). 0.1 é o default. Aumente quando achar que o agente está combinando sinais irrelevantes." />
        </strong>
        <p className="text-xs text-muted-foreground">
          Maior = regras mais curtas e fáceis de ler. Menor = o agente tem mais liberdade pra
          combinar sinais.
        </p>
        <input
          type="range"
          min={0}
          max={0.5}
          step={0.05}
          value={parsimony}
          onChange={(e) => setParsimony(Number(e.target.value))}
        />
        <span className="tabular-nums">{parsimony.toFixed(2)}</span>
      </div>
      <div className="optimizer-refit-actions">
        <Button
          size="sm"
          onClick={() => {
            const hints: RefitHints = {};
            if (blacklist.size > 0) hints.blacklistedFeatures = Array.from(blacklist);
            if (lockedGrades.size > 0 && proposal.proposed) {
              hints.lockedRungs = proposal.proposed.rungs
                .filter((r) => lockedGrades.has(r.grade))
                .map((r) => ({ grade: r.grade, when: r.when, description: r.description }));
            }
            if (parsimony !== 0.1) hints.parsimony = parsimony;
            if (Object.keys(hints).length === 0) {
              toast({
                title: "Sem mudanças",
                description:
                  "Marque pelo menos um sinal, uma regra para preservar, ou mexa na simplicidade das regras.",
              });
              return;
            }
            onSubmit(hints);
          }}
        >
          <RotateCw className="h-3.5 w-3.5 mr-2" aria-hidden />
          Refazer
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
